// eslint-disable-next-line simple-import-sort/imports
import 'reflect-metadata';
import 'react-toastify/dist/ReactToastify.css';
import '@fontsource/ibm-plex-sans/100.css';
import '@fontsource/ibm-plex-sans/200.css';
import '@fontsource/ibm-plex-sans/300.css';
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-sans/100-italic.css';
import '@fontsource/ibm-plex-sans/200-italic.css';
import '@fontsource/ibm-plex-sans/300-italic.css';
import '@fontsource/ibm-plex-sans/400-italic.css';
import '@fontsource/ibm-plex-sans/500-italic.css';
import '@fontsource/ibm-plex-sans/600-italic.css';
import '@fontsource/ibm-plex-sans/700-italic.css';
import { IAuthStore } from './__legacy__/modules/auth/auth.store';
import { IBrowserService } from './__legacy__/services/browser.service';
import { ICacheManager } from './__legacy__/services/cacheManager';
import { IStorageService } from './__legacy__/services/storage.service';
import { IUndoRedoHistory } from './__legacy__/modules/annotation/undoRedoHistory.service';
import { IUserService } from './__legacy__/modules/user/user.service';
import { Router } from 'react-router';
import { AlertBarContainer } from './modules/alertBar/AlertBar.container';
import { IMainApiService } from './__legacy__/services/api.service';
import { ErrorsContainer } from './__legacy__/modules/errors/containers/Errors.container';
import { Loader } from './__legacy__/components/Loader';
import { OverlayLoaderContainer } from './__legacy__/containers/OverlayLoader.container';
import { SettingsContextProvider } from './modules/settings/SettingsContext';
import { ACDLError } from './__legacy__/models/error.model';
import { container } from './diContainer';
import { enableDevTools } from './__legacy__/helpers/devtools';
import { config as fontAwesomeConfig } from '@fortawesome/fontawesome-svg-core';
import { AdminContextProvider } from './modules/admin/AdminContext';
import { ToastContainer } from 'react-toastify';
import { withPolling } from './__legacy__/helpers/polling.helpers';
import { IApiServiceTraining } from './__legacy__/services/api.service.training';
import { ReactElement, useEffect, useRef } from 'react';
import { useLoader } from './__legacy__/helpers/useLoader';
import { AppRoutes } from './AppRoutes';
import { ThemeProvider } from './ThemeProvider';
import { MonkeyPatchLeaflet } from './MonkeyPatchLeaflet';
import { ICurrentWorkspaceBl } from './modules/workspaces/currentWorkspace/CurrentWorkspace.bl';
import { Provider, useInjection } from 'inversify-react';
import { IAppConfig } from './configuration/config.types';
import { browserHistory } from './usehistory';
import { configure } from 'mobx';
import { initLocalization } from './modules/localization/i18n';
import { PlanUpgradeModal } from './modules/plans/planUpgrageModal';
import { useConfiguration } from 'common/hooks/useConfiguration';

configure({
  enforceActions: 'never',
});

MonkeyPatchLeaflet();
enableDevTools();

const AppPure = (): ReactElement => {
  const apiService = useInjection(IMainApiService.$);
  const apiTrainingService = useInjection(IApiServiceTraining.$);

  const cacheManager = useInjection(ICacheManager.$);
  const undoRedoHistory = useInjection(IUndoRedoHistory.$);
  const browserService = useInjection(IBrowserService.$);
  const userService = useInjection(IUserService.$);
  const authStore = useInjection(IAuthStore.$);
  const storageService = useInjection(IStorageService.$);
  const currentWorkspaceBl = useInjection(ICurrentWorkspaceBl.$);

  const configuration = useConfiguration();

  const clearPolling = useRef<() => void>(null);
  const { isLoading, withLoaderAsync } = useLoader(true);

  useEffect(() => {
    void initializeApp();
    return (): void => clearPolling.current?.();
  }, []);

  const initializeApp = async (): Promise<void> => {
    currentWorkspaceBl.Initialize();

    fontAwesomeConfig.autoAddCss = false;

    storageService.initializeLocalStorage();

    browserService.addOnBeforeUnload(() => undoRedoHistory.canUndo);

    await loadConfig();

    if (!configuration.appConfig.runLocally) {
      setupTrainingManagerPing();
    }

    await initLocalization();
  };

  const loadConfig = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      await cacheManager.cleanupOldEntriesAsync();
      const result = await apiService.getAsync<IAppConfig>('/Config');

      if (result instanceof ACDLError) {
        throw result;
      }

      configuration.setupAppConfig(result);

      if (authStore.isAuthenticated) {
        await Promise.all([userService.getUserInfoAsync(), userService.getUserSettingsAsync()]);
      }

      await storageService.initializeCryptoLocalStorage();
    });
  };

  const setupTrainingManagerPing = (): void => {
    if (!clearPolling.current) {
      const url = '/ping-training-manager';

      clearPolling.current = withPolling(
        async (): Promise<void> => {
          await apiTrainingService.postAsync(url);
        },
        1000 * 60 * 3
      ); // ping every 3 minutes
    }
  };

  return (
    <ThemeProvider>
      <Loader isLoading={isLoading}>
        {!isLoading && (
          <SettingsContextProvider>
            <AdminContextProvider>
              <OverlayLoaderContainer />
              <Router history={browserHistory}>
                <ErrorsContainer>
                  <AlertBarContainer />
                  <PlanUpgradeModal />
                  <ToastContainer autoClose={5000} />
                  <AppRoutes />
                </ErrorsContainer>
              </Router>
            </AdminContextProvider>
          </SettingsContextProvider>
        )}
      </Loader>
    </ThemeProvider>
  );
};

export const App = (): ReactElement => (
  <Provider container={container}>
    <AppPure />
  </Provider>
);
